'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';

import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { MOTION_POSITION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// CVA size variants
// ---------------------------------------------------------------------------

function createPillVariants(): ReturnType<
  typeof cva<{
    size: { default: string; sm: string };
  }>
> {
  return cva(
    'relative appearance-none inline-flex shrink-0 items-center justify-center rounded-pill font-medium whitespace-nowrap outline-none transition-colors',
    {
      variants: {
        size: {
          default: 'h-9 gap-1.5 text-body px-4',
          sm: 'h-7 gap-1 text-body px-3',
        },
      },
      defaultVariants: { size: 'default' },
    },
  );
}

function createPillIconVariants(): ReturnType<
  typeof cva<{ size: { default: string; sm: string } }>
> {
  return cva('relative z-10 shrink-0', {
    variants: {
      size: {
        default: 'size-icon-md',
        sm: 'size-icon-sm',
      },
    },
    defaultVariants: { size: 'default' },
  });
}

const pillVariants: ReturnType<typeof createPillVariants> = createPillVariants();
const pillIconVariants: ReturnType<typeof createPillIconVariants> = createPillIconVariants();

const PREFIX_PADDING_BY_SIZE = {
  default: 'pl-3',
  sm: 'pl-2',
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SortPillOption {
  label: string;
  value: string;
  prefix?: IconSvgElement | undefined;
  prefixNode?: ReactNode;
}

export interface SortPillRowProps extends VariantProps<typeof pillVariants> {
  options: SortPillOption[];
  activeValue: string;
  onChange: (value: string) => void;
  /** Optional analytics callback fired when the active value changes. */
  onChangeEvent?: ((value: string, previous: string) => void) | undefined;
  /**
   * Unique layoutId for the active-pill background animation. Required when
   * multiple `SortPillRow` instances render on the same page so they don't
   * share the same shared-layout animation channel.
   */
  layoutId?: string | undefined;
  /** Optional className applied to the row container. */
  className?: string | undefined;
  /** ARIA role for the container — defaults to `tablist`. */
  role?: 'tablist' | 'group' | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Pill-row picker — the visual language used by the markets feed sort bar
 * (`@/components/shared/sort-filter-bar`). Single active state with a
 * smoothly-animated pill background via Motion's `layoutId`.
 *
 * `size` variants:
 * - `default` — h-9, full body type, used by the markets / proposals feed.
 * - `sm` — h-7, used by the global search modal tabs.
 */
export function SortPillRow({
  options,
  activeValue,
  onChange,
  onChangeEvent,
  size,
  layoutId = 'sort-pill-row-active',
  className,
  role = 'tablist',
}: SortPillRowProps): React.ReactNode {
  const prefersReducedMotion = useReducedMotion();

  function handleClick(value: string): void {
    if (value !== activeValue) {
      onChangeEvent?.(value, activeValue);
      onChange(value);
    }
  }

  return (
    <LazyMotion features={domMax}>
      <div
        className={cn('flex gap-0 overflow-x-auto overflow-y-visible scrollbar-none', className)}
        role={role}
      >
        {options.map((option) => {
          const isActive = activeValue === option.value;
          const hasPrefix = option.prefix !== undefined || option.prefixNode !== undefined;
          return (
            <m.button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={cn(
                pillVariants({ size }),
                hasPrefix && PREFIX_PADDING_BY_SIZE[size ?? 'default'],
                isActive
                  ? 'text-foreground-brand dark:text-foreground-primary'
                  : 'text-foreground-tertiary hover:text-foreground-primary',
              )}
              onClick={(): void => handleClick(option.value)}
            >
              {isActive && (
                <m.span
                  layoutId={layoutId}
                  className={cn(
                    'pointer-events-none absolute inset-0 z-0 rounded-pill bg-background-brand',
                    'dark:bg-background-primary',
                  )}
                  transition={prefersReducedMotion ? { duration: 0 } : MOTION_POSITION}
                />
              )}
              {option.prefix !== undefined && (
                <Icon icon={option.prefix} className={pillIconVariants({ size })} />
              )}
              {option.prefixNode !== undefined ? (
                <span className="relative z-10 shrink-0">{option.prefixNode}</span>
              ) : null}
              <span className="relative z-10">{option.label}</span>
            </m.button>
          );
        })}
      </div>
    </LazyMotion>
  );
}
